import { ContractSelect } from "../contracts/ContractSelect";
import { CurrentDropDown } from "../../currency/CurrencyDropDown";
import { EditableDate } from "../../core/EditableDate";
import { EditableListItemNumber } from "../../core/EditableListItemText";
import { EditableSwitch } from "../../core/EditableSwitch";
import { EmptyDetailCard } from "../../core/EmptyDetailCard";
import { formatCurrency, formatNumber, useUserCurrencyFormatter } from "./currency-formatting";
import { RoyaltyTypeSelect } from "./RoyaltyTypeSelect";
import {
  useAllRoyalties,
  useDeleteRoyaltyEvent,
  useGame,
  useGameRoyalties,
  useMyGameEditions,
  useUpdateRoyaltyEvent,
} from "@seabrookstudios/pitch2table";
import { useGameIdFromRoute, useRoyaltyIdFromRoute } from "../WithGameIdFromRoute";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import React from "react";
import Stack from "@mui/material/Stack";
import { Path } from "../../Path";
import Typography from "@mui/material/Typography";
import { AlwaysOnAppLink } from "../../styleguide/AppLink";
import { useYearFromParam } from "../../royalties/params";
import { PublisherSelect } from "./PublisherSelect";
import { useDocumentTitle } from "usehooks-ts";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArrowListItem } from "../../core/ArrowListItem";
import { EditionSelect } from "./EditionSelect";

export const RoyaltyFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const royaltyId = useRoyaltyIdFromRoute();
  const { data: royalties = [] } = useGameRoyalties(gameId);
  const { data: editions = [] } = useMyGameEditions(gameId);
  const royalty = royalties.find((r) => r.id === royaltyId);
  const updateRoyaltyEvent = useUpdateRoyaltyEvent(gameId, royaltyId);
  const deleteRoyaltyEvent = useDeleteRoyaltyEvent(gameId, royaltyId);
  const formatUserCurrency = useUserCurrencyFormatter();
  const navigate = useNavigate();

  useDocumentTitle(royalty ? royalty.type : "Pitch2Table");

  if (!royalty) {
    return <EmptyDetailCard text="Select a royalty event from the list on the left." />;
  }

  /**
   * @param {number} date
   */
  const onUpdateDate = async (date) => {
    await updateRoyaltyEvent.mutateAsync({
      date,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {number} amount
   */
  const onUpdateAmount = async (amount) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {boolean} paid
   */
  const togglePaid = async (paid) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").Currency} currency
   */
  const onUpdateCurrency = async (currency) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").RoyaltyType} type
   */
  const onUpdateType = async (type) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId|"unset"} contractId
   */
  const onUpdateContractId = async (contractId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").GameEditionId} gameEditionId
   */
  const onUpdateGameEditionId = async (gameEditionId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      gameEditionId: gameEditionId,
      receiptId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId|"unset"} receiptId
   */
  const onUpdateReceiptId = async (receiptId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId|"unset"} publisherId
   */
  const onUpdatePublisherId = async (publisherId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {number} units
   */
  const onUpdateUnits = async (units) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  const deleteRoyalty = async () => {
    await deleteRoyaltyEvent.mutateAsync();
    navigate(Path.game(gameId).royalties());
  };

  return (
    <Card elevation={1} square={false}>
      <CardContent>
        <List>
          <Stack direction="row">
            <ListItem>
              <EditableListItemNumber
                primary={royalty.paid ? "Amount (payment currency)" : "Amount due"}
                secondary={royalty.amount}
                formatter={formatCurrency(royalty.currency)}
                onSave={onUpdateAmount}
                isBusy={updateRoyaltyEvent.isPending}
                helperText={royalty.paid ? "The amount of the payment." : "The expected amount of the payment."}
              />
            </ListItem>
            {royalty.paid && (
              <ListItem>
                <ListItemText
                  primary="Amount (user currency)"
                  secondary={formatUserCurrency(royalty.convertedAmount)}
                />
              </ListItem>
            )}
          </Stack>
          <ListItem>
            <EditableListItemNumber
              primary="Units"
              secondary={royalty.units || 0}
              formatter={formatNumber}
              onSave={onUpdateUnits}
              isBusy={updateRoyaltyEvent.isPending}
              helperText="Number of units this payment represents (e.g. copies sold)"
            />
          </ListItem>
          <ListItem>
            <EditableDate
              title={royalty.paid ? "Date paid" : "Due date"}
              timestamp={royalty.date}
              onSave={onUpdateDate}
              isBusy={updateRoyaltyEvent.isPending}
            />
          </ListItem>
          <ListItem>
            <CurrentDropDown
              value={royalty.currency}
              onChange={onUpdateCurrency}
              helperText="The currency of the payment was made in."
            />
          </ListItem>
          <ListItem>
            <RoyaltyTypeSelect
              value={royalty.type}
              onChange={onUpdateType}
              helperText="What kind of royalty payment is this e.g. Advance, Signing Bonus, Royalty"
            />
          </ListItem>
          <ListItem>
            <PublisherSelect
              gameId={royalty.gameId}
              value={royalty.publisherId || "unset"}
              onChange={onUpdatePublisherId}
              helperText={<Typography variant="inherit">Which publisher did this payment come from?</Typography>}
            />
          </ListItem>
          <ListItem>
            <EditionSelect
              gameId={royalty.gameId}
              value={royalty.gameEditionId}
              onChange={onUpdateGameEditionId}
              helperText="Which edition/release does this payment relate to?"
              disabled={editions.length === 0}
            />
          </ListItem>
          <ListItem>
            <ContractSelect
              gameId={gameId}
              value={royalty.contractId || "unset"}
              onChange={onUpdateContractId}
              allowTypes={["co-designer", "designer-publisher", "nda", "other"]}
              label="Contract"
              helperText={
                <Typography variant="inherit">
                  Which contract is this payment in relation to.{" "}
                  {royalty.contractId !== "unset" && royalty.contractId ? (
                    <AlwaysOnAppLink to={Path.game(royalty.gameId).contract(royalty.contractId)}>
                      View contract
                    </AlwaysOnAppLink>
                  ) : (
                    <AlwaysOnAppLink to={Path.game(royalty.gameId).contracts()}>Upload contract</AlwaysOnAppLink>
                  )}
                </Typography>
              }
            />
          </ListItem>
          <ListItem>
            <EditableSwitch
              initialValue={royalty.paid}
              onLabel="Paid"
              offLabel="Not paid"
              title="Has you received this payment?"
              onUpdate={togglePaid}
            />
          </ListItem>
          <ListItem>
            <ContractSelect
              gameId={gameId}
              value={royalty.receiptId || "unset"}
              onChange={onUpdateReceiptId}
              allowTypes={["receipt"]}
              label="Royalty Statement"
              helperText={
                <Typography variant="inherit">
                  Select the secure document that is the royalty statement for this payment.{" "}
                  {royalty.receiptId !== "unset" && royalty.receiptId ? (
                    <AlwaysOnAppLink to={Path.game(royalty.gameId).contract(royalty.receiptId)}>
                      View royalty statement
                    </AlwaysOnAppLink>
                  ) : (
                    <AlwaysOnAppLink to={Path.game(royalty.gameId).contracts()}>Upload secure document</AlwaysOnAppLink>
                  )}
                </Typography>
              }
            />
          </ListItem>
        </List>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <IconButton onClick={deleteRoyalty}>
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export const RoyaltyFromRouteSansGame = () => {
  const year = useYearFromParam();
  const royaltyId = useRoyaltyIdFromRoute();
  const { data: royalties = [] } = useAllRoyalties();
  const royalty = royalties.find((r) => r.id === royaltyId);
  const updateRoyaltyEvent = useUpdateRoyaltyEvent(royalty ? royalty.gameId : undefined, royaltyId);
  const deleteRoyaltyEvent = useDeleteRoyaltyEvent(royalty ? royalty.gameId : undefined, royaltyId);
  const formatUserCurrency = useUserCurrencyFormatter();
  const { data: game } = useGame(royalty ? royalty.gameId : undefined);
  const { data: editions = [] } = useMyGameEditions(royalty ? royalty.gameId : undefined);
  const navigate = useNavigate();

  useDocumentTitle(royalty ? royalty.type : "Pitch2Table");

  if (!royalty) {
    return <EmptyDetailCard text="Select a royalty event from the list on the left." />;
  }

  /**
   * @param {number} date
   */
  const onUpdateDate = async (date) => {
    await updateRoyaltyEvent.mutateAsync({
      date,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {number} amount
   */
  const onUpdateAmount = async (amount) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {boolean} paid
   */
  const togglePaid = async (paid) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").Currency} currency
   */
  const onUpdateCurrency = async (currency) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").RoyaltyType} type
   */
  const onUpdateType = async (type) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId|"unset"} contractId
   */
  const onUpdateContractId = async (contractId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId|"unset"} receiptId
   */
  const onUpdateReceiptId = async (receiptId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId|"unset"} publisherId
   */
  const onUpdatePublisherId = async (publisherId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {number} units
   */
  const onUpdateUnits = async (units) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      receiptId: undefined,
      gameEditionId: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").GameEditionId} gameEditionId
   */
  const onUpdateGameEditionId = async (gameEditionId) => {
    await updateRoyaltyEvent.mutateAsync({
      date: undefined,
      amount: undefined,
      units: undefined,
      type: undefined,
      paid: undefined,
      currency: undefined,
      contractId: undefined,
      publisherId: undefined,
      gameEditionId: gameEditionId,
      receiptId: undefined,
    });
  };

  const deleteRoyalty = async () => {
    await deleteRoyaltyEvent.mutateAsync();

    if (!year) {
      navigate(Path.royalties().category("by-year"));
      return;
    }

    navigate(Path.royalties().byYear(year).all());
  };

  return (
    <Card elevation={1} square={false}>
      <List>
        {game && <ArrowListItem primary="Game" secondary={game.name} to={Path.game(royalty.gameId).link()} />}
        <Stack direction="row">
          <ListItem>
            <EditableListItemNumber
              primary={royalty.paid ? "Amount (payment currency)" : "Amount due"}
              secondary={royalty.amount}
              formatter={formatCurrency(royalty.currency)}
              onSave={onUpdateAmount}
              isBusy={updateRoyaltyEvent.isPending}
              helperText={royalty.paid ? "The amount of the payment." : "The expected amount of the payment."}
            />
          </ListItem>
          {royalty.paid && (
            <ListItem>
              <ListItemText primary="Amount (user currency)" secondary={formatUserCurrency(royalty.convertedAmount)} />
            </ListItem>
          )}
        </Stack>
        <ListItem>
          <EditableListItemNumber
            primary="Units"
            secondary={royalty.units || 0}
            formatter={formatNumber}
            onSave={onUpdateUnits}
            isBusy={updateRoyaltyEvent.isPending}
            helperText="Number of units this payment represents (e.g. copies sold)"
          />
        </ListItem>
        <ListItem>
          <EditableDate
            title={royalty.paid ? "Date paid" : "Due date"}
            timestamp={royalty.date}
            onSave={onUpdateDate}
            isBusy={updateRoyaltyEvent.isPending}
          />
        </ListItem>
        <ListItem>
          <CurrentDropDown
            value={royalty.currency}
            onChange={onUpdateCurrency}
            helperText="The currency of the payment was made in."
          />
        </ListItem>
        <ListItem>
          <RoyaltyTypeSelect
            value={royalty.type}
            onChange={onUpdateType}
            helperText="What kind of royalty payment is this e.g. Advance, Signing Bonus, Royalty"
          />
        </ListItem>
        <ListItem>
          <PublisherSelect
            gameId={royalty.gameId}
            value={royalty.publisherId || "unset"}
            onChange={onUpdatePublisherId}
            helperText={<Typography variant="inherit">Which publisher did this payment come from?</Typography>}
          />
        </ListItem>
        <ListItem>
          <EditionSelect
            gameId={royalty.gameId}
            value={royalty.gameEditionId}
            onChange={onUpdateGameEditionId}
            helperText="Which edition/release does this payment relate to?"
            disabled={editions.length === 0}
          />
        </ListItem>
        <ListItem>
          <ContractSelect
            gameId={royalty.gameId}
            value={royalty.contractId || "unset"}
            onChange={onUpdateContractId}
            allowTypes={["co-designer", "designer-publisher", "nda", "other"]}
            label="Contract"
            helperText={
              <Typography variant="inherit">
                Which contract is this payment in relation to.{" "}
                {royalty.contractId !== "unset" && royalty.contractId ? (
                  <AlwaysOnAppLink to={Path.game(royalty.gameId).contract(royalty.contractId)}>
                    View contract
                  </AlwaysOnAppLink>
                ) : (
                  <AlwaysOnAppLink to={Path.game(royalty.gameId).contracts()}>Upload contract</AlwaysOnAppLink>
                )}
              </Typography>
            }
          />
        </ListItem>
        <ListItem>
          <EditableSwitch
            initialValue={royalty.paid}
            onLabel="Paid"
            offLabel="Not paid"
            title="Has you received this payment?"
            onUpdate={togglePaid}
          />
        </ListItem>
        <ListItem>
          <ContractSelect
            gameId={royalty.gameId}
            value={royalty.receiptId || "unset"}
            onChange={onUpdateReceiptId}
            allowTypes={["receipt"]}
            label="Royalty Statement"
            helperText={
              <Typography variant="inherit">
                Select the secure document that is the royalty statement for this payment.{" "}
                {royalty.receiptId !== "unset" && royalty.receiptId ? (
                  <AlwaysOnAppLink to={Path.game(royalty.gameId).contract(royalty.receiptId)}>
                    View royalty statement
                  </AlwaysOnAppLink>
                ) : (
                  <AlwaysOnAppLink to={Path.game(royalty.gameId).contracts()}>Upload secure document</AlwaysOnAppLink>
                )}
              </Typography>
            }
          />
        </ListItem>
      </List>
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <IconButton onClick={deleteRoyalty}>
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
};
